import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Section, SectionHeader } from "@/components/site/Section";
import { AdvisorCTA } from "@/components/site/AdvisorCTA";
import boardroomAssetSrc from "@/assets/boardroom.png";
import { SITE_URL } from "@/lib/seo";
import {
  blogPosts,
  blogTopics,
  featuredPost,
  formatPostDate,
  TOTAL_POSTS,
  type BlogPost,
} from "@/content/blog";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    links: [{ rel: "canonical", href: `${SITE_URL}/blog/` }],
    meta: [
      { title: "Leadership Insights and Resources | Leadership Edge Live" },
      {
        name: "description",
        content:
          "Practical guidance on leadership development, written by L&D professionals with 20+ years of real-world experience.",
      },
      { property: "og:title", content: "Leadership Insights and Resources | Leadership Edge Live" },
      {
        property: "og:description",
        content:
          "Practical guidance on leadership development, written by L&D professionals with 20+ years of real-world experience.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Blog,
});

function Meta({ post }: { post: BlogPost }) {
  // Each date/read-time item carries its own leading divider. The row is shifted
  // left by one divider width inside an overflow-hidden box, so whichever item
  // starts a line has its divider clipped — no dangling "|" when it wraps.
  const divider = <span className="inline-block w-4 text-center text-border">|</span>;
  return (
    <div className="mt-6 flex items-center gap-3">
      <img
        src={post.authorAvatar}
        alt=""
        loading="lazy"
        className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
      />
      <div className="min-w-0 text-xs text-muted-foreground">
        <p>{post.author}</p>
        <div className="overflow-hidden">
          <p className="-ml-4 flex flex-wrap">
            <span className="whitespace-nowrap">
              {divider}
              {formatPostDate(post.date)}
            </span>
            <span className="whitespace-nowrap">
              {divider}
              {post.readTime}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}

function Blog() {
  const [topic, setTopic] = useState<string | null>(null);
  const more = blogPosts.filter((p) => !topic || p.category === topic);

  return (
    <>
      <PageHero
        maxWidth="6xl"
        headline={
          <>
            Leadership insights <span className="text-[#693D94]">and resources.</span>
          </>
        }
        supporting="Practical guidance on leadership development, written by L&D professionals with 20+ years of real-world experience."
      />

      <Section tone="default">
        <SectionHeader
          eyebrow="This week's read"
          title="Our latest piece is handpicked for managers navigating today's hardest moments."
          titleClassName="text-2xl font-semibold leading-snug text-ink md:text-3xl"
          titleStyle={{ fontFamily: "'Poppins', sans-serif" }}
        />

        <Link
          to="/blog/$slug/"
          params={{ slug: featuredPost.slug }}
          className="group grid overflow-hidden rounded-2xl border border-border/60 bg-background transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-brand md:grid-cols-2"
        >
          <div className="aspect-[16/10] w-full overflow-hidden bg-lavender md:aspect-auto md:h-full">
            <img
              src={featuredPost.image}
              alt={featuredPost.imageAlt}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>
          <div className="flex flex-col justify-center p-7 md:p-10">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">
              {featuredPost.category}
            </p>
            <h3 className="mt-3 font-display text-2xl leading-snug text-ink md:text-3xl">
              {featuredPost.title}
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              {featuredPost.excerpt}
            </p>
            <Meta post={featuredPost} />
            <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
              Read article
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </span>
          </div>
        </Link>
      </Section>

      <Section tone="lavender">
        <div className="grid gap-12 lg:grid-cols-[2fr_1fr]">
          <div>
            <SectionHeader
              title={topic ?? "All articles"}
              titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
              titleStyle={{ fontFamily: "'Poppins', sans-serif" }}
            />
            <div className="grid gap-6 sm:grid-cols-2">
              {more.map((post) => (
                <Link
                  key={post.slug}
                  to="/blog/$slug/"
                  params={{ slug: post.slug }}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border/60 bg-background transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-brand"
                >
                  <div className="aspect-[16/10] w-full overflow-hidden bg-lavender">
                    <img
                      src={post.image}
                      alt={post.imageAlt}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                      {post.category}
                    </p>
                    <h3 className="mt-2 font-display text-lg leading-snug text-ink">
                      {post.title}
                    </h3>
                    <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                      {post.excerpt}
                    </p>
                    <div className="mt-auto">
                      <Meta post={post} />
                      <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
                        Read article
                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <aside className="lg:sticky lg:top-28 lg:max-h-[calc(100vh-8rem)] lg:self-start lg:overflow-y-auto">
            <h3 className="font-sans text-xs font-semibold uppercase tracking-wider text-primary">
              Browse by topic
            </h3>
            <ul className="mt-5 space-y-1">
              <li>
                <button
                  type="button"
                  onClick={() => setTopic(null)}
                  className={`flex w-full items-center justify-between rounded-[6px] px-3 py-2 text-sm transition-colors hover:bg-background ${
                    topic === null
                      ? "bg-background font-semibold text-ink"
                      : "text-muted-foreground"
                  }`}
                >
                  All topics <span className="text-xs text-muted-foreground">({TOTAL_POSTS})</span>
                </button>
              </li>
              {blogTopics.map((t) => (
                <li key={t.slug}>
                  <button
                    type="button"
                    onClick={() => setTopic(t.label)}
                    className={`flex w-full items-center justify-between rounded-[6px] px-3 py-2 text-left text-sm transition-colors hover:bg-background hover:text-primary ${
                      topic === t.label
                        ? "bg-background font-semibold text-ink"
                        : "text-muted-foreground"
                    }`}
                  >
                    {t.label} <span className="text-xs">({t.count})</span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </Section>

      <AdvisorCTA
        title="Want help turning any of this into practice?"
        body="A Development Pro can point you to the behavior worth working on first."
        backgroundImage={boardroomAssetSrc}
      />
    </>
  );
}
